import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"
import { useOrganization } from "../OrganizationContext"
import { useUserRole } from "../UserRoleContext"
import { COMPANY_DISPLAY_NAME } from "../constants/branding"
import {
  BILLING_FORBIDDEN_MESSAGE,
  initializeStripeCheckout,
} from "../api/stripeBilling"
import { changeOrganizationType } from "../api/organizations"
import "./OrganizationAdmin.css"
import { getValidSupabaseAccessToken } from "../utils/supabaseSession"
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui"

const CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
})

const cx = (...classes) => classes.filter(Boolean).join(" ")

const createPricingDefaults = () => ({
  price_per_store_cents: null,
  price_override_cents: null,
  discount_percent: null,
  plan: null,
})

const parseStoredNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }
  return null
}

const formatCurrencyFromCents = (cents) => {
  const numeric = parseStoredNumber(cents)
  if (numeric === null) {
    return "—"
  }
  return CURRENCY_FORMATTER.format(numeric / 100)
}

const clampPercentage = (value) => Math.min(Math.max(value, 0), 100)

function InfoTooltip({ text, ariaLabel }) {
  const [open, setOpen] = useState(false)

  const show = () => setOpen(true)
  const hide = () => setOpen(false)
  const toggle = () => setOpen((prev) => !prev)

  return (
    <span className="ui-tooltip">
      <button
        type="button"
        aria-label={ariaLabel}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={toggle}
        className="ui-icon-button"
      >
        <span aria-hidden="true">i</span>
      </button>
      {open && (
        <span role="tooltip" className="ui-tooltip__bubble">
          {text}
        </span>
      )}
    </span>
  )
}

export default function OrganizationAdmin() {
  const { id } = useParams()
  const navigate = useNavigate()
  const user = useUser()
  const platformRole = useUserRole()
  const { organizations, refreshOrganizations } = useOrganization()
  const queryClient = useQueryClient()
  const [inviteEmail, setInviteEmail] = useState("")
  const [isEditingName, setIsEditingName] = useState(false)
  const [nameValue, setNameValue] = useState("")
  const [nameError, setNameError] = useState("")
  const [billingStatus, setBillingStatus] = useState(null)
  const [isBillingRedirecting, setIsBillingRedirecting] = useState(false)

  const redirectToStripeBilling = useCallback(
    (checkout) => {
      if (
        !checkout ||
        !checkout.url ||
        (checkout.flow !== "checkout_session" &&
          checkout.flow !== "billing_portal")
      ) {
        setBillingStatus({
          type: "error",
          message: "We couldn't open Stripe billing. Please try again.",
        })
        return false
      }

      if (
        typeof window === "undefined" ||
        typeof window.location?.assign !== "function"
      ) {
        setBillingStatus({
          type: "error",
          message: "Stripe checkout is only available in a web browser.",
        })
        return false
      }

      const isPortalFlow = checkout.flow === "billing_portal"

      setBillingStatus({
        type: "info",
        message: isPortalFlow
          ? "Opening Stripe billing portal…"
          : "Opening Stripe checkout…",
      })

      window.location.assign(checkout.url)
      return true
    },
    [setBillingStatus],
  )

  const fetchMembers = async () => {
    if (!supabase.supabaseKey) {
      console.warn("Supabase API key missing; skipping member fetch")
      return []
    }
    let { data, error } = await supabase.rpc("get_organization_users", {
      org_id: id,
    })
    if (error && error.code === "42883") {
      const res = await supabase
        .from("organization_members")
        .select("role, user:auth.users(id, email, raw_user_meta_data)")
        .eq("organization_id", id)
      data = res.data?.map((row) => ({
        id: row.user.id,
        email: row.user.email,
        name:
          row.user.raw_user_meta_data?.name ||
          row.user.raw_user_meta_data?.full_name ||
          "",
        role: row.role,
      }))
      error = res.error
    }
    if (error) {
      console.error("Failed to fetch members", error)
      return []
    }
    return data || []
  }

  const fetchInvites = async () => {
    const { data, error } = await supabase
      .from("organization_invites")
      .select("id, email")
      .eq("organization_id", id)
    if (error) {
      console.error("Failed to fetch invites", error)
      return []
    }
    return data || []
  }

  const {
    data: members = [],
    isLoading: membersLoading,
  } = useQuery({
    queryKey: ["organization-members", id],
    queryFn: fetchMembers,
    enabled: !!user && !!id,
  })

  const {
    data: invites = [],
    isLoading: invitesLoading,
  } = useQuery({
    queryKey: ["organization-invites", id],
    queryFn: fetchInvites,
    enabled: !!user && !!id,
  })

  const {
    data: dataSharing = {
      share_with_product_vendors: false,
      share_with_platform: false,
    },
    isLoading: dataSharingLoading,
  } = useQuery({
    queryKey: ["organization-data-sharing", id],
    queryFn: async () => {
      if (!supabase.supabaseKey) {
        console.warn("Supabase API key missing; skipping data sharing fetch")
        return {
          share_with_product_vendors: false,
          share_with_platform: false,
        }
      }
      const { data, error } = await supabase
        .from("organizations")
        .select("share_with_product_vendors, share_with_platform")
        .eq("id", id)
      if (error) {
        console.error("Failed to fetch data sharing settings", error)
        return {
          share_with_product_vendors: false,
          share_with_platform: false,
        }
      }
      return (data && data[0]) || {
        share_with_product_vendors: false,
        share_with_platform: false,
      }
    },
    enabled: !!user && !!id,
  })

  const {
    data: pricingSettingsData,
    isLoading: pricingLoading,
  } = useQuery({
    queryKey: ["organization-pricing", id],
    queryFn: async () => {
      if (!supabase.supabaseKey) {
        console.warn("Supabase API key missing; skipping pricing fetch")
        return createPricingDefaults()
      }
      const { data, error } = await supabase
        .from("organizations")
        .select(
          "price_per_store_cents, price_override_cents, discount_percent, plan:plans(name, default_price_cents)",
        )
        .eq("id", id)
        .maybeSingle()
      if (error) {
        console.error("Failed to fetch pricing settings", error)
        return createPricingDefaults()
      }
      return data ?? createPricingDefaults()
    },
    enabled: !!user && !!id,
  })

  const pricingSettings = useMemo(
    () => pricingSettingsData ?? createPricingDefaults(),
    [pricingSettingsData],
  )

  const planDefaultCents = useMemo(
    () =>
      parseStoredNumber(
        (Array.isArray(pricingSettings?.plan)
          ? pricingSettings.plan[0]
          : pricingSettings?.plan
        )?.default_price_cents,
      ),
    [pricingSettings?.plan],
  )

  const overrideCents = useMemo(
    () => parseStoredNumber(pricingSettings?.price_override_cents),
    [pricingSettings?.price_override_cents],
  )

  const customPriceCents = useMemo(
    () => parseStoredNumber(pricingSettings?.price_per_store_cents),
    [pricingSettings?.price_per_store_cents],
  )

  const discountPercentValue = useMemo(
    () => parseStoredNumber(pricingSettings?.discount_percent),
    [pricingSettings?.discount_percent],
  )

  const effectivePriceCents = useMemo(() => {
    const base =
      typeof overrideCents === "number"
        ? overrideCents
        : typeof customPriceCents === "number"
          ? customPriceCents
          : 10000
    const clamped =
      typeof discountPercentValue === "number"
        ? clampPercentage(discountPercentValue)
        : 0
    return Math.max(0, Math.round(base * ((100 - clamped) / 100)))
  }, [overrideCents, customPriceCents, discountPercentValue])

  const organizationSummary = useMemo(() => {
    return organizations.find((org) => String(org.id) === String(id)) || null
  }, [organizations, id])

  useEffect(() => {
    setNameValue(organizationSummary?.name || "")
  }, [organizationSummary?.name])

  const currentPlanName = organizationSummary?.plan?.name || "free"
  const currentPlanLabel = useMemo(() => {
    if (!currentPlanName) return "Free"
    const original = String(currentPlanName)
    const normalized = original.toLowerCase()
    const labels = {
      free: "Free",
      paid: "Paid",
      admin: "Admin",
      enterprise: "Enterprise",
    }
    if (labels[normalized]) {
      return labels[normalized]
    }
    return original.charAt(0).toUpperCase() + original.slice(1)
  }, [currentPlanName])

  const loading = membersLoading || invitesLoading

  const updateOrgNameMutation = useMutation({
    mutationFn: async (newName) => {
      const { data, error } = await supabase
        .from("organizations")
        .update({ name: newName })
        .eq("id", id)
        .select("id, name")
        .single()
      if (error) {
        throw error
      }
      return data
    },
    onSuccess: (updated) => {
      setIsEditingName(false)
      setNameError("")
      setNameValue(updated?.name || "")
      queryClient.invalidateQueries({
        queryKey: ["user-organizations", user?.id],
      })
      if (typeof refreshOrganizations === "function") {
        refreshOrganizations()
      }
    },
    onError: (error) => {
      console.error("Failed to update organization name", error)
      setNameError(
        "Unable to update the organization name. Please try again."
      )
    },
  })

  const dataSharingMutation = useMutation({
    mutationFn: async (updates) => {
      const { data, error } = await supabase.rpc("set_org_data_sharing", {
        p_org_id: id,
        p_share_with_product_vendors: updates.share_with_product_vendors,
        p_share_with_platform: updates.share_with_platform,
      })
      if (error) {
        throw error
      }
      const updated = Array.isArray(data) ? data[0] : data
      return updated || updates
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(["organization-data-sharing", id], updated)
      queryClient.invalidateQueries({
        queryKey: ["organization-data-sharing", id],
      })
    },
    onError: (error) => {
      console.error("Failed to update data sharing settings", error)
      alert("Failed to update data sharing settings. Please try again.")
    },
  })

  const organizationTypeMutation = useMutation({
    mutationFn: async (organizationType) => {
      await changeOrganizationType(id, organizationType)
      return organizationType
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-organizations", user?.id] })
      refreshOrganizations?.()
    },
    onError: (error) => {
      console.error("Failed to update organization type", error)
      alert("Unable to update the organization type. Check billing before trying again.")
    },
  })

  const handleRoleChange = async (userId, newRole) => {
    const { error } = await supabase
      .from("organization_members")
      .update({ role: newRole })
      .eq("organization_id", id)
      .eq("user_id", userId)
    if (error) {
      console.error("Failed to update role", error)
    } else {
      await queryClient.invalidateQueries({
        queryKey: ["organization-members", id],
      })
    }
  }

  const isAdmin = members.some((m) => m.id === user?.id && m.role === "admin")
  const canManageBilling = members.some(
    (m) => m.id === user?.id && (m.role === "admin" || m.role === "billing"),
  )
  const canEditName = isAdmin && !membersLoading
  const isSavingName = updateOrgNameMutation.isLoading
  const handleManageBilling = useCallback(async () => {
    if (!id) return
    if (membersLoading || !canManageBilling) {
      setBillingStatus({
        type: "error",
        message:
          "Only organization admins or billing managers can manage billing.",
      })
      return
    }
    if (typeof window === "undefined" || !window.location) {
      setBillingStatus({
        type: "error",
        message: "Billing management is only available in a web browser.",
      })
      return
    }
    if (isBillingRedirecting) return

    setBillingStatus(null)
    setIsBillingRedirecting(true)
    try {
      const checkout = await initializeStripeCheckout(id, {
        origin: window.location.origin,
        flow: "billing_portal",
      })

      redirectToStripeBilling(checkout)
    } catch (error) {
      console.error("Failed to open Stripe billing", error)
      if (error?.status === 403) {
        setBillingStatus({ type: "error", message: BILLING_FORBIDDEN_MESSAGE })
      } else {
        const normalizedMessage =
          error?.message || "We couldn't open Stripe billing. Please try again."
        setBillingStatus({
          type: "error",
          message: normalizedMessage,
        })
      }
    } finally {
      setIsBillingRedirecting(false)
    }
  }, [
    canManageBilling,
    id,
    isBillingRedirecting,
    membersLoading,
    redirectToStripeBilling,
  ])

  const handleStartEditingName = () => {
    if (!canEditName) return
    setIsEditingName(true)
    setNameError("")
  }

  const handleCancelEditName = () => {
    setIsEditingName(false)
    setNameError("")
    setNameValue(organizationSummary?.name || "")
  }

  const handleSaveName = () => {
    if (!canEditName) return
    const trimmed = nameValue.trim()
    if (!trimmed) {
      setNameError("Organization name cannot be empty.")
      return
    }
    if (organizationSummary && trimmed === organizationSummary.name) {
      setIsEditingName(false)
      setNameError("")
      setNameValue(organizationSummary.name)
      return
    }
    setNameError("")
    updateOrgNameMutation.mutate(trimmed)
  }

  const displayName = (nameValue && nameValue.trim()) || "Untitled organization"

  const handleDataSharingToggle = (field, value) => {
    if (!isAdmin || dataSharingLoading || dataSharingMutation.isLoading) return
    const current = dataSharing || {
      share_with_product_vendors: false,
      share_with_platform: false,
    }
    const updates = {
      share_with_product_vendors:
        field === "share_with_product_vendors"
          ? value
          : current.share_with_product_vendors,
      share_with_platform:
        field === "share_with_platform" ? value : current.share_with_platform,
    }
    dataSharingMutation.mutate(updates)
  }

  const handleInvite = async () => {
    const email = inviteEmail.trim().toLowerCase()
    if (!email) return
    const token = await getValidSupabaseAccessToken(supabase)
    const { data, error } = await supabase.functions.invoke("invite-user", {
      body: { org_id: id, invite_email: email },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (error) {
      let message = ""
      try {
        message = await error.context.text()
      } catch (readErr) {
        console.error("Failed to read error body", readErr)
      }
      console.error("Failed to invite user", error, message)
      alert(`Failed to invite user: ${message}`)
    } else {
      setInviteEmail("")
      await queryClient.invalidateQueries({
        queryKey: ["organization-members", id],
      })
      await queryClient.invalidateQueries({
        queryKey: ["organization-invites", id],
      })
      if (!data?.userExists) {
        alert(
          "User not found. Ask them to sign in, then contact support so we can " +
            "finish setting up their invite."
        )
      }
    }
  }

  const handleRemoveInvite = async (inviteId) => {
    const { error } = await supabase
      .from("organization_invites")
      .delete()
      .eq("id", inviteId)
    if (error) {
      console.error("Failed to delete invite", error)
    } else {
      await queryClient.invalidateQueries({
        queryKey: ["organization-invites", id],
      })
    }
  }

  const handleRemoveMember = async (userId) => {
    const { error } = await supabase
      .from("organization_members")
      .delete()
      .eq("organization_id", id)
      .eq("user_id", userId)
    if (error) {
      console.error("Failed to remove member", error)
    } else {
      await queryClient.invalidateQueries({
        queryKey: ["organization-members", id],
      })
    }
  }

  return (
    <div className="page-container page-container--wide ui-stack ui-stack--xl">
      <Card>
        <CardHeader split>
          <CardTitle className="organization-admin__name-title">
            {displayName}
          </CardTitle>
          <div className="ui-stack ui-stack--row ui-stack--xs organization-admin__header-actions">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate("/organizations/manage")}
            >
              Manage organizations
            </Button>
            {canEditName && !isEditingName && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleStartEditingName}
              >
                Edit Name
              </Button>
            )}
          </div>
        </CardHeader>
        {isEditingName ? (
          <CardContent>
            <div className="ui-stack ui-stack--sm organization-admin__name-form">
              <Input
                label="Organization name"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                disabled={isSavingName}
                error={nameError}
              />
              {isSavingName && (
                <p className="ui-text--muted">Saving organization name...</p>
              )}
            </div>
          </CardContent>
        ) : !organizationSummary ? (
          <CardContent>
            <p className="ui-text--muted">
              Organization details are not available.
            </p>
          </CardContent>
        ) : (
          <CardContent>
            <div className="ui-stack ui-stack--xs">
              <span className="ui-text--muted">Organization type</span>
              {platformRole === "admin" ? (
                <select
                  aria-label="Organization type"
                  value={organizationSummary.organizationType || "automotive"}
                  onChange={(event) => organizationTypeMutation.mutate(event.target.value)}
                  disabled={organizationTypeMutation.isLoading}
                >
                  <option value="automotive">Automotive</option>
                  <option value="standard">Standard business</option>
                </select>
              ) : (
                <Badge variant="outline">
                  {organizationSummary.organizationType === "standard"
                    ? "Standard business"
                    : "Automotive"}
                </Badge>
              )}
              <p className="ui-text--muted">
                This setting controls account audit rules and billing terminology. Contact support to change it.
              </p>
              <span className="ui-text--muted">
                {organizationSummary?.customAuditRulesEnabled ||
                organizationSummary?.custom_audit_rules_enabled
                  ? "Organization-specific audit rules are enabled for this subscription."
                  : "This organization uses the platform audit rules."}
              </span>
            </div>
          </CardContent>
        )}
        {isEditingName && (
          <CardFooter className="ui-stack ui-stack--row ui-stack--wrap organization-admin__name-actions">
            <Button
              onClick={handleSaveName}
              disabled={isSavingName || !nameValue.trim()}
            >
              Save Name
            </Button>
            <Button
              variant="ghost"
              onClick={handleCancelEditName}
              disabled={isSavingName}
            >
              Cancel
            </Button>
          </CardFooter>
        )}
      </Card>

      <Card>
        <CardHeader className="organization-admin__plan-header">
          <div className="ui-stack ui-stack--xs organization-admin__plan-copy">
            <CardTitle>Current Plan</CardTitle>
            <CardDescription>
              Review your subscription level. Contact Supabase if you need
              changes.
            </CardDescription>
          </div>
          <div className="organization-admin__plan-badge">
            <Badge variant="accent" size="xl">
              {currentPlanLabel}
            </Badge>
          </div>
        </CardHeader>
        <CardFooter className="ui-stack ui-stack--sm organization-admin__plan-actions">
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            onClick={() => navigate(`/organizations/${id}/plan`)}
          >
            Change Plan
          </Button>
          <Button
            variant="outline"
            size="sm"
            fullWidth
            onClick={handleManageBilling}
            disabled={
              membersLoading || !canManageBilling || isBillingRedirecting
            }
          >
            {isBillingRedirecting ? "Opening Stripe…" : "Manage Billing"}
          </Button>
          {!membersLoading && !canManageBilling && (
            <p className="organization-admin__billing-note">
              Only admins or billing managers can access Stripe billing.
            </p>
          )}
          {billingStatus && (
            <p
              className={cx(
                "organization-admin__billing-message",
                billingStatus.type === "error"
                  ? "organization-admin__billing-message--error"
                  : billingStatus.type === "info"
                    ? "organization-admin__billing-message--info"
                    : "organization-admin__billing-message--success",
              )}
              role={billingStatus.type === "error" ? "alert" : "status"}
            >
              {billingStatus.message}
            </p>
          )}
        </CardFooter>
      </Card>

      {organizationSummary?.plan?.ga4_enabled !== false && (
        <Card>
          <CardHeader>
            <CardTitle>GA4 standards and access</CardTitle>
            <CardDescription>
              Organization-wide templates and access controls apply across account workspaces.
            </CardDescription>
          </CardHeader>
          <CardContent className="ui-button-row">
            <Button variant="outline" onClick={() => navigate("/ga4-template")}>Manage GA4 templates</Button>
            <Button variant="outline" onClick={() => navigate("/ga4-access")}>Manage GA4 access</Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Pricing Overview</CardTitle>
          <CardDescription>
            Review your organization's current pricing details.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pricingLoading ? (
            <p className="ui-text--muted">Loading pricing settings...</p>
          ) : (
            <div className="ui-stack ui-stack--md">
              <div className="organization-admin__pricing-summary">
                <div className="organization-admin__pricing-summary-item">
                  <p className="organization-admin__pricing-summary-label">
                    Plan default
                  </p>
                  <p className="organization-admin__pricing-summary-value">
                    {typeof planDefaultCents === "number"
                      ? formatCurrencyFromCents(planDefaultCents)
                      : "Not set"}
                  </p>
                </div>
                <div className="organization-admin__pricing-summary-item">
                  <p className="organization-admin__pricing-summary-label">
                    Effective price
                  </p>
                  <p className="organization-admin__pricing-summary-value">
                    {typeof effectivePriceCents === "number"
                      ? formatCurrencyFromCents(effectivePriceCents)
                      : "Not set"}
                  </p>
                </div>
              </div>
              <p className="ui-text--muted">
                Pricing is managed by Supabase administrators. For billing
                assistance, please contact your Supabase organization admin.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Organization Data Sharing</CardTitle>
          <CardDescription>
            Decide how {COMPANY_DISPLAY_NAME} collaborates with trusted partners.
          </CardDescription>
        </CardHeader>
        <CardContent className="ui-stack ui-stack--md">
          {dataSharingLoading ? (
            <p className="ui-text--muted">Loading data sharing settings...</p>
          ) : (
            <>
              <Checkbox
                label="Share with product vendors"
                labelAdornment={
                  <InfoTooltip
                    ariaLabel="Learn more about sharing data with product vendors"
                    text={`Allows ${COMPANY_DISPLAY_NAME} to share relevant GA4 data for selected products with the respective product vendors to troubleshoot issues and ensure data accuracy.`}
                  />
                }
                description="Enable access for vendor partners when they need to troubleshoot tagging."
                checked={!!dataSharing.share_with_product_vendors}
                onChange={(e) =>
                  handleDataSharingToggle(
                    "share_with_product_vendors",
                    e.target.checked
                  )
                }
                disabled={
                  dataSharingMutation.isLoading ||
                  dataSharingLoading ||
                  membersLoading ||
                  !isAdmin
                }
              />
              <Checkbox
                label="Share with platform"
                labelAdornment={
                  <InfoTooltip
                    ariaLabel="Learn more about sharing data with the platform"
                    text={`Helps ${COMPANY_DISPLAY_NAME} build industry benchmarks and evaluate vendor performance so dealers can make informed decisions across the platform.`}
                  />
                }
                description="Allow anonymized usage insights to improve recommendations across the platform."
                checked={!!dataSharing.share_with_platform}
                onChange={(e) =>
                  handleDataSharingToggle(
                    "share_with_platform",
                    e.target.checked
                  )
                }
                disabled={
                  dataSharingMutation.isLoading ||
                  dataSharingLoading ||
                  membersLoading ||
                  !isAdmin
                }
              />
              {dataSharingMutation.isLoading && (
                <p className="ui-text--muted">
                  Saving data sharing preferences...
                </p>
              )}
              {!membersLoading && !isAdmin && (
                <p className="ui-text--muted">
                  Only organization admins can update data sharing preferences.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader split>
          <div className="ui-stack ui-stack--xs">
            <CardTitle>Organization Members</CardTitle>
            <CardDescription>
              Manage who can access {COMPANY_DISPLAY_NAME}.
            </CardDescription>
          </div>
          <Badge variant="outline">
            {loading ? "Loading…" : `${members.length} members`}
          </Badge>
        </CardHeader>
        <CardContent className="ui-stack ui-stack--lg">
          {loading ? (
            <p className="ui-text--muted">Loading members…</p>
          ) : members.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Email</TableHeaderCell>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Role</TableHeaderCell>
                    <TableHeaderCell align="right">Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>{m.email}</TableCell>
                      <TableCell>{m.name}</TableCell>
                      <TableCell>
                        <select
                          className="ui-select"
                          value={m.role}
                          onChange={(e) =>
                            handleRoleChange(m.id, e.target.value)
                          }
                        >
                          <option value="member">Analyst</option>
                          <option value="admin">Agency administrator</option>
                          <option value="billing">Billing (read-only monitoring)</option>
                          <option value="read_only">Read-only</option>
                        </select>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleRemoveMember(m.id)}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <p className="ui-text--muted">
              You do not have permission to view members.
            </p>
          )}

          <div className="ui-form-inline">
            <Input
              type="email"
              label="Invite by email"
              placeholder="user@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
            <Button onClick={handleInvite} disabled={!inviteEmail.trim()}>
              Invite User
            </Button>
          </div>

          {invites.length > 0 && (
            <div className="ui-stack ui-stack--md">
              <h3 className="ui-heading-sm">Pending Invites</h3>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Email</TableHeaderCell>
                      <TableHeaderCell align="right">Actions</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invites.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell>{inv.email}</TableCell>
                        <TableCell align="right">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemoveInvite(inv.id)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
